import React, { useEffect, useState } from "react";
import { docFileName } from "@/lib/utils";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PrintMenu from "@/components/PrintMenu";
import { Plus, Send, SearchCheck, Inbox, Link2 } from "lucide-react";
import { Link } from "react-router-dom";
import { EmitPurchaseEcfDialog } from "@/components/EmitPurchaseEcfDialog";
import { PurchaseFormDialog } from "@/components/purchases/PurchaseFormDialog";
import { formatDOP, formatDate } from "@/lib/format";

export default function Purchases() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [emitFor, setEmitFor] = useState(null);

  const load = async () => {
    setItems((await api.get("/purchases")).data);
  };

  useEffect(() => { load(); }, []);

  return (
    <PageContainer>
      <PageHeader
        title="Compras"
        subtitle="Compras formales e informales (E41), con o sin entrada al inventario y retenciones según la ley."
        testid="purchases-header"
        actions={<div className="flex gap-2">
          <Link to="/ecf-recibidos"><Button variant="outline" data-testid="purchases-ecf-recibidos-btn"><Inbox className="w-4 h-4 mr-1.5" /> e-CF recibidos</Button></Link>
          <Button onClick={() => setOpen(true)} className="bg-primary font-bold" data-testid="new-purchase-btn"><Plus className="w-4 h-4 mr-1.5" /> Nueva compra</Button>
        </div>}
      />

      <PurchaseFormDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Número</th>
              <th className="text-left p-3">Proveedor</th>
              <th className="text-left p-3">Ref</th>
              <th className="text-left p-3">Fecha</th>
              <th className="text-right p-3">Total</th>
              <th className="text-left p-3">e-CF</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 data-cell font-bold">{p.number}</td>
                <td className="p-3">{p.supplier_name}</td>
                <td className="p-3 data-cell text-xs">
                  {p.supplier_ncf || p.invoice_ref}
                  {p.ecf_recibido_id && (
                    <span className={`ml-1.5 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded font-bold ${p.ecf_verified ? "bg-success/15 text-success" : "bg-amber-100 text-amber-800"}`}
                      title={p.ecf_verified ? "Enlazado con el e-CF recibido" : `Enlazado con el e-CF recibido · diferencia ${formatDOP(p.ecf_amount_diff)}`} data-testid={`purchase-ecf-linked-${p.id}`}>
                      <Link2 className="w-3 h-3" />{p.ecf_verified ? "e-CF" : "Revisar"}
                    </span>
                  )}
                </td>
                <td className="p-3 text-xs text-muted-foreground">{formatDate(p.created_at)}</td>
                <td className="p-3 text-right data-cell font-bold">{formatDOP(p.total)}</td>
                <td className="p-3 text-xs">
                  {p.ncf ? (
                    <span className="font-mono">{p.ncf}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                  {p.ecf_status && (
                    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${
                      p.ecf_status === "aceptado" ? "bg-success/15 text-success" :
                      p.ecf_status === "rechazado" || p.ecf_status === "error" ? "bg-destructive/15 text-destructive" :
                      "bg-muted text-muted-foreground"
                    }`}>{p.ecf_status}</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end items-center">
                    <Button
                      size="icon" variant="ghost"
                      data-testid={`purchase-send-ecf-${p.id}`}
                      title={p.ecf_status === "aceptado" ? "Ya aceptado por DGII (re-emitir solo en pruebas)" : "Emitir e-CF y enviar a DGII"}
                      onClick={() => setEmitFor(p)}
                    >
                      <Send className="w-4 h-4 text-primary" />
                    </Button>
                    {p.is_electronic && p.ecf_track_id && (
                      <Button
                        size="icon" variant="ghost"
                        data-testid={`purchase-verify-ecf-${p.id}`}
                        title="Verificar estado en DGII (trackId)"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/dgii/phase4/refresh-status/${p.ncf}`);
                            const est = data.new_status || "desconocido";
                            if (est === "aceptado") toast.success(`DGII: Aceptado · ${p.ncf}`);
                            else if (est === "rechazado") toast.error(`DGII: Rechazado · ${data.error || p.ncf}`);
                            else toast.message(`DGII: ${est} · ${p.ncf}`);
                            load();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Error verificando DGII");
                          }
                        }}
                      >
                        <SearchCheck className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    )}
                    {p.ecf_status && p.ecf_status !== "error" && p.ecf_status !== "rechazado" && (
                      <a href={`${import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL}/api/purchases/${p.id}/ecf-xml`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost" data-testid={`purchase-xml-${p.id}`}>XML</Button>
                      </a>
                    )}
                    <PrintMenu
                      url={`/purchases/${p.id}/pdf`}
                      filename={docFileName(p)}
                      label=""
                      iconOnly
                      variant="ghost"
                      size="icon"
                      testid={`purchase-pdf-${p.id}`}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Sin compras</td></tr>}
          </tbody>
        </table>
      </div>
      {emitFor && <EmitPurchaseEcfDialog purchase={emitFor} onClose={() => setEmitFor(null)} onDone={() => { setEmitFor(null); load(); }} />}
    </PageContainer>
  );
}
